// Shared validation utilities for resume forms (SubmitResume, ResumeProfile)

/**
 * Ensure user has mandatory qualifications: TENTH, TWELFTH, DEGREE
 */
export function validateRequiredQualifications(educations = []) {
  const qualificationCounts = educations.reduce((acc, edu) => {
    if (edu && edu.qualification) {
      acc[edu.qualification] = (acc[edu.qualification] || 0) + 1;
    }
    return acc;
  }, {});

  const required = [
    { value: 'TENTH', label: 'Secondary' },
    { value: 'TWELFTH', label: 'Higher Secondary' },
    { value: 'DEGREE', label: "Bachelor's Degree" },
  ];

  const missing = required.filter((r) => !qualificationCounts[r.value]);
  if (missing.length > 0) {
    const missingLabels = missing.map((m) => m.label);
    return {
      isValid: false,
      message: `Please add ${missingLabels.join(', ')} ${
        missingLabels.length > 1 ? 'qualifications' : 'qualification'
      } to proceed`,
      errors: {
        educations: {
          qualification: {
            type: 'manual',
            message: `Missing ${missingLabels.join(', ')}`,
          },
        },
      },
    };
  }
  return { isValid: true };
}

/**
 * Prevent duplicates for school-level entries: only one TENTH and one TWELFTH
 */
export function validateDuplicateSchoolQualifications(educations = []) {
  const counts = educations.reduce((acc, edu) => {
    if (edu && edu.qualification) {
      acc[edu.qualification] = (acc[edu.qualification] || 0) + 1;
    }
    return acc;
  }, {});

  if (counts.TENTH > 1 || counts.TWELFTH > 1) {
    return {
      isValid: false,
      message:
        counts.TENTH > 1
          ? 'Secondary qualification can only be added once'
          : 'Higher Secondary qualification can only be added once',
      errors: {
        educations: {
          qualification: {
            type: 'manual',
            message:
              counts.TENTH > 1
                ? 'Secondary qualification can only be added once'
                : 'Higher Secondary qualification can only be added once',
          },
        },
      },
    };
  }
  return { isValid: true };
}

/**
 * True when `errors.educations[i]` has at least one field error.
 * Field order matches top-to-bottom focus order in EducationFields.
 */
export function educationRowHasError(eduErr) {
  if (!eduErr || typeof eduErr !== 'object') return false;
  return !!(
    eduErr.qualification ||
    eduErr.institution ||
    eduErr.graduationYear ||
    eduErr.fieldsOfStudy ||
    eduErr.board ||
    eduErr.university ||
    eduErr.maxMarks ||
    eduErr.earnedMarks ||
    eduErr.maxGPA ||
    eduErr.earnedGPA
  );
}

/** Index of the first education row with a field error, or -1. */
export function getFirstEducationErrorRowIndex(educationErrors) {
  if (!Array.isArray(educationErrors)) return -1;
  return educationErrors.findIndex((e) => educationRowHasError(e));
}

/**
 * Maps react-hook-form `errors.educations` array to a single toast-friendly message.
 */
/** Ordered keys for collapsed error tags (matches EducationFields focus order). */
export const EDUCATION_ERROR_FIELD_ORDER = [
  'qualification',
  'institution',
  'graduationYear',
  'board',
  'university',
  'fieldsOfStudy',
  'maxMarks',
  'earnedMarks',
  'maxGPA',
  'earnedGPA',
];

/**
 * Human-readable labels for education validation tags (aligned with education-validation HTML).
 */
export function getEducationErrorTagLabels(eduErr) {
  if (!eduErr || typeof eduErr !== 'object') return [];
  const tags = [];
  let hasMaxScoreTag = false;
  let hasScoredTag = false;
  for (const k of EDUCATION_ERROR_FIELD_ORDER) {
    if (!eduErr[k]) continue;
    if (k === 'maxMarks' || k === 'maxGPA') {
      if (!hasMaxScoreTag) {
        tags.push('Max Score');
        hasMaxScoreTag = true;
      }
    } else if (k === 'earnedMarks' || k === 'earnedGPA') {
      if (!hasScoredTag) {
        tags.push('Score');
        hasScoredTag = true;
      }
    } else {
      const map = {
        qualification: 'Qualification',
        institution: 'Institution',
        graduationYear: 'Year',
        board: 'Board',
        university: 'University',
        fieldsOfStudy: 'Field of Study',
      };
      tags.push(map[k] || k);
    }
  }
  return tags;
}

/** Count education rows that have at least one field error. */
export function countEducationRowsWithErrors(educationErrors) {
  if (!Array.isArray(educationErrors)) return 0;
  return educationErrors.filter((e) => educationRowHasError(e)).length;
}

/** Total number of field-level errors across all education rows. */
export function countMissingEducationFields(educationErrors) {
  if (!Array.isArray(educationErrors)) return 0;
  let n = 0;
  for (const row of educationErrors) {
    if (!row || typeof row !== 'object') continue;
    for (const k of EDUCATION_ERROR_FIELD_ORDER) {
      if (row[k]) n += 1;
    }
  }
  return n;
}

/**
 * Toast when save fails education validation (matches HTML attemptSave copy).
 */
export function getEducationAttentionToastMessage(eduErrors) {
  const n = countEducationRowsWithErrors(eduErrors);
  if (n <= 0) {
    return 'Please fill in all required fields for education';
  }
  return `${n} ${n === 1 ? 'entry needs' : 'entries need'} attention`;
}

export function getEducationValidationToastMessage(eduErrors) {
  if (!Array.isArray(eduErrors)) {
    return 'Please fill in all required fields for education';
  }
  const firstErrorIndex = eduErrors.findIndex((err) => err);
  if (firstErrorIndex < 0) {
    return 'Please fill in all required fields for education';
  }
  const fe = eduErrors[firstErrorIndex];
  if (!fe) {
    return 'Please fill in all required fields for education';
  }
  const msg = (field, fallback) => fe[field]?.message || fallback;
  if (fe.qualification)
    return msg('qualification', 'Please select a qualification');
  if (fe.institution) return msg('institution', 'Institution name is required');
  if (fe.graduationYear) return msg('graduationYear', 'Year is required');
  if (fe.fieldsOfStudy)
    return msg('fieldsOfStudy', 'Field of Study is required');
  if (fe.board) return msg('board', 'Board name is required');
  if (fe.university) return msg('university', 'University is required');
  if (fe.maxMarks) return msg('maxMarks', 'Max marks is required');
  if (fe.earnedMarks) return msg('earnedMarks', 'Score is required');
  if (fe.maxGPA) return msg('maxGPA', 'Max GPA is required');
  if (fe.earnedGPA) return msg('earnedGPA', 'Score is required');
  return `Please fill in all required fields for education entry #${firstErrorIndex + 1}`;
}

/**
 * Preferences validation; at least one preferred location
 */
export function validatePreferences(preferredLocations) {
  if (!preferredLocations || preferredLocations.length === 0) {
    return {
      isValid: false,
      errors: {
        preferredLocations: {
          type: 'manual',
          message: 'At least one location is required',
        },
      },
    };
  }
  return { isValid: true };
}

/**
 * Aggregate validation to run before submit. Returns { isValid, errors?, message? }
 */
export function validateResumeForm(data) {
  const aggregatedErrors = {};

  // Required qualifications
  const req = validateRequiredQualifications(data.educations || []);
  if (!req.isValid) {
    return req; // Early return with specific message
  }

  // Duplicate checks for TENTH/TWELFTH
  const dup = validateDuplicateSchoolQualifications(data.educations || []);
  if (!dup.isValid) {
    return dup; // Early return with specific message
  }

  // Preferences (keep light since field-level also validates)
  const pref = validatePreferences(data.preferredLocations || []);
  if (!pref.isValid) {
    Object.assign(aggregatedErrors, pref.errors);
  }

  if (Object.keys(aggregatedErrors).length > 0) {
    return { isValid: false, errors: aggregatedErrors };
  }

  return { isValid: true };
}
